import React, { useState, useEffect, useContext } from 'react'
import axios from 'axios'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams, Link } from 'react-router-dom'
import {
  Container,
  Button,
  FormGroup,
  FormControl,
  FormLabel,
  Image,
  Row,
  Col,
} from 'react-bootstrap'
import DatePicker, { registerLocale } from 'react-datepicker'
import 'react-datepicker/dist/react-datepicker.css'
import fr from 'date-fns/locale/fr'

import PoiStatusSelect from '../components/PointOfInterest/PoiStatusSelect'
import PoiTypeSelect from '../components/PointOfInterest/PoiTypeSelect'
import CitySelect from '../components/CitySelect'
import SurfaceTypeSelect from '../components/SurfaceTypeSelect'
import TypeSelect from '../components/TypeSelect'
import TechnicitySelect from '../components/TechnicitySelect'
import GoogleMapEdit from '../components/PointOfInterest/GoogleMapEdit'
import NavigationBar from '../components/NavigationBar'
import Footer from '../components/Footer/Footer'
import ManagePoiNavBar from '../components/PointOfInterest/ManagePoiNavBar'
import config from '../config/config'
import {
  POI_PATH,
  LOGIN_PATH,
  MANAGE_POI_EDIT_PATH,
  MANAGE_EVENT_EDIT_PATH,
  EVENT_PATH,
} from '../config/routes'
import { AuthContext } from '../contexts/AuthContext'
import { slugify } from '../utils/utils'
import { useAuth } from '../contexts/AuthContext'
import {
  getTechnicity,
  getSurfaceType,
  getType,
  getAltitude,
} from '../utils/utils'
import CityMultiSelect from '../components/CityMultiSelect'
import PoiTypeMultiSelect from '../components/PoiTypeMultiSelect'
import { useWebsite, setFlashMessage } from '../contexts/WebsiteContext'

registerLocale('fr', fr)

const ManagePoiEdit = ({ isEvent }) => {
  const { logout } = useContext(AuthContext)
  const { token } = useAuth()
  const { t } = useTranslation()

  const { id } = useParams()
  const navigate = useNavigate()
  const isNew = !id

  const [isPoiReady, setIsPoiReady] = useState(isNew)
  const [poi, setPoi] = useState({
    isEvent: isEvent,
    poiType: { name: '' },
    status: '',
    city: '',
    labelFr: '',
    labelEn: '',
    labelEs: '',
    descriptionFr: '',
    descriptionEn: '',
    descriptionEs: '',
    youtubeVideoUrl: '',
    technicity: '',
    surfaceType: '',
    type: '',
    altitude: '',
    phone: '',
    email: '',
    gpsx: config.mapCenter.lat,
    gpsy: config.mapCenter.lng,
    startDate: '',
    endDate: '',
  })

  const [formData, setFormData] = useState({
    linkCriteriaPoiTypes: [],
    linkCriteriaCitys: [],
    linkCriteriaDistance: 50000,
  })

  const [poiStatus, setPoiStatus] = useState('')
  const [poiType, setPoiType] = useState('')
  const [city, setCity] = useState('')
  const [labels, setLabels] = useState({
    labelFr: '',
    labelEn: '',
    labelEs: '',
  })
  const [descriptions, setDescriptions] = useState({
    descriptionFr: '',
    descriptionEn: '',
    descriptionEs: '',
  })
  const [youtubeVideoUrl, setYoutubeVideoUrl] = useState('')
  const [technicity, setTechnicity] = useState('')
  const [surfaceType, setSurfaceType] = useState('')
  const [type, setType] = useState('')
  const [altitude, setAltitude] = useState('')
  const [phone, setPhone] = useState('')
  const [email, setEmail] = useState('')
  const [startDate, setStartDate] = useState(null)
  const [endDate, setEndDate] = useState(null)
  const [gpsx, setGpsx] = useState(poi.gpsx)
  const [gpsy, setGpsy] = useState(poi.gpsy)
  const [isMountainBikingPoi, setIsMountainBikingPoi] = useState(false)
  const [isWildlifePoi, setIsWildlifePoi] = useState(false)
  const [hasAltitudePoi, setHasAltitudePoi] = useState(false)
  const [hasPhoneAndEmailPoi, setHasPhoneAndEmailPoi] = useState(false)
  const [citys, setCitys] = useState([])
  const [poiTypes, setPoiTypes] = useState([])
  const [filteredPoiTypes, setFilteredPoiTypes] = useState([])
  const [distance, setDistance] = useState(50000)

  useEffect(() => {
    const fetchPoi = async () => {
      try {
        if (!isNew) {
          const response = await axios.get(`${config.apiUrl}poi/${id}`)
          const fetchedPoi = response.data

          if (fetchedPoi.startDate) {
            setStartDate(new Date(fetchedPoi.startDate))
          } else {
            setStartDate(null)
          }

          if (fetchedPoi.endDate) {
            setEndDate(new Date(fetchedPoi.endDate))
          } else {
            setEndDate(null)
          }

          setPoi(fetchedPoi)
          setPoiStatus(fetchedPoi.status)
          setPoiType(fetchedPoi.poiType.name)
          setCity(fetchedPoi.city.name)
          setLabels({
            labelFr: fetchedPoi.labelFr,
            labelEn: fetchedPoi.labelEn,
            labelEs: fetchedPoi.labelEs,
          })
          setDescriptions({
            descriptionFr: fetchedPoi.descriptionFr,
            descriptionEn: fetchedPoi.descriptionEn,
            descriptionEs: fetchedPoi.descriptionEs,
          })
          setYoutubeVideoUrl(
            fetchedPoi.youtubeVideoUrl ? fetchedPoi.youtubeVideoUrl : '',
          )
          setGpsx(fetchedPoi.gpsx)
          setGpsy(fetchedPoi.gpsy)
          setTechnicity(getTechnicity(fetchedPoi))
          setSurfaceType(getSurfaceType(fetchedPoi))
          setAltitude(getAltitude(fetchedPoi))
          setType(getType(fetchedPoi))
        }
        setIsPoiReady(true)
      } catch (error) {
        // console.error('An error occurred:', error);
        setFlashMessage(t('ERROR_MESSAGE_API_GET'), 'error')
      }
    }

    fetchPoi()
  }, [id, t, isNew])

  useEffect(() => {
    const fetchCitys = async () => {
      try {
        const response = await axios.get(`${config.apiUrl}city`)
        setCitys(response.data)
      } catch (error) {
        // console.error('An error occurred:', error);
        setFlashMessage(t('ERROR_MESSAGE_API_GET'), 'error')
      }
    }

    const fetchPoiTypes = async () => {
      try {
        const response = await axios.get(`${config.apiUrl}poi-type`)
        setPoiTypes(response.data)
      } catch (error) {
        // console.error('An error occurred:', error);
        setFlashMessage(t('ERROR_MESSAGE_API_GET'), 'error')
      }
    }

    fetchCitys()
    fetchPoiTypes()
  }, [t])

  useEffect(() => {
    const filterPoiTypes = () => {
      setFilteredPoiTypes(poiTypes.filter((type) => type.isEvent === isEvent))
    }

    filterPoiTypes()
  }, [poiTypes, isEvent])

  const handleSaveChanges = async () => {
    try {
      const data = {
        poiType: poiType,
        status: poiStatus,
        city: city,
        labelFr: labels.labelFr,
        labelEn: labels.labelEn,
        labelEs: labels.labelEs,
        descriptionFr: descriptions.descriptionFr,
        descriptionEn: descriptions.descriptionEn,
        descriptionEs: descriptions.descriptionEs,
        gpsx: gpsx,
        gpsy: gpsy,
        youtubeVideoUrl: youtubeVideoUrl,
        technicity: technicity,
        surfaceType: surfaceType,
        type: type,
        email: email,
        phone: phone,
        altitude: altitude === '' ? 0 : parseInt(altitude, 10),
        startDate: startDate,
        endDate: endDate,
        isEvent: isEvent,
        linkCriteriaPoiTypes: formData.linkCriteriaPoiTypes,
        linkCriteriaCitys: formData.linkCriteriaCitys,
        linkCriteriaDistance: formData.linkCriteriaDistance,
      }

      if (isNew) {
        try {
          const response = await axios.post(`${config.apiUrl}poi`, data, {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          })
          const newPoiId = response.data.id
          setFlashMessage(t('SUCCESS_CREATED_MESSAGE'), 'success')

          const redirectPath = isEvent
            ? MANAGE_EVENT_EDIT_PATH
            : MANAGE_POI_EDIT_PATH
          navigate(
            `${redirectPath
              .replace(':slug', slugify(data.labelFr))
              .replace(':id', newPoiId)}`,
          )
        } catch (error) {
          if (error.response && error.response.status === 401) {
            logout()
            navigate(LOGIN_PATH)
          }
          // console.error('An error occurred:', error);
          setFlashMessage(t('ERROR_MESSAGE_API_GET'), 'error')
        }
      } else {
        try {
          await axios.put(`${config.apiUrl}poi/${id}`, data, {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          })
          setFlashMessage(t('SUCCESS_UPDATED_MESSAGE'), 'success')
        } catch (error) {
          //console.error('Error webservice call:', error);

          if (error.response && error.response.status === 401) {
            logout()
            navigate(LOGIN_PATH)
          } else if (error.response) {
            console.error('An error occurred PUT POI:', id, error)
            setFlashMessage(t('ERROR_MESSAGE'), 'error')
          }
        }
      }
    } catch (error) {
      console.error('An error occurred:', error)
      setFlashMessage(t('ERROR_MESSAGE_API_POST'), 'error')
    }
  }

  const onUpdateLocation = (newLocation) => {
    setGpsx(newLocation.gpsx)
    setGpsy(newLocation.gpsy)
  }

  const getTitle = (isNew, isEvent, poi) => {
    if (isNew && isEvent) {
      return t('EVENT_ADD')
    }

    if (isNew) {
      return t('POI_ADD')
    }

    return `${poi.labelFr} - Mode édition`
  }

  useEffect(() => {
    setIsMountainBikingPoi(poiType === 'MOUNTAIN_BIKING')
    setIsWildlifePoi(poiType === 'WILDLIFE')
    setHasAltitudePoi(config.poiTypesWithAltitude.includes(poiType))
    setHasPhoneAndEmailPoi(config.poiTypesWithPhoneAndEmail.includes(poiType))
  }, [poi, poiType])

  const handlePoiTypesChange = (name) => {
    setFormData((prevFormData) => {
      const updatedPoiTypes = prevFormData.linkCriteriaPoiTypes.includes(name)
        ? prevFormData.linkCriteriaPoiTypes.filter((item) => item !== name)
        : [...prevFormData.linkCriteriaPoiTypes, name]
      return { ...prevFormData, linkCriteriaPoiTypes: updatedPoiTypes }
    })
  }

  const handleCitysChange = (name) => {
    setFormData((prevFormData) => {
      const updatedCitys = prevFormData.linkCriteriaCitys.includes(name)
        ? prevFormData.linkCriteriaCitys.filter((item) => item !== name)
        : [...prevFormData.linkCriteriaCitys, name]
      return { ...prevFormData, linkCriteriaCitys: updatedCitys }
    })
  }

  const handleDistanceChange = (event) => {
    const selectedDistance = event.target.value
    setDistance(selectedDistance)
    setFormData({ ...formData, linkCriteriaDistance: selectedDistance })
  }

  return (
    <>
      <NavigationBar />

      <ManagePoiNavBar id={id} isNew={isNew} />
      <Container>
        <h1>{getTitle(isNew, isEvent, poi)}</h1>
        <Link
          to={`${(poi.isEvent ? EVENT_PATH : POI_PATH)
            .replace(':slug', slugify(poi.labelFr))
            .replace(':id', poi.id)}`}
          className="btn btn-primary"
          style={{ margin: '10px' }}
        >
          {t('SHOW')}
        </Link>

        {isPoiReady && (
          <>
            <h2 style={{ marginTop: '30px' }}>Informations générales</h2>
            <Row className="margin-top-20">
              <Col>
                <PoiStatusSelect
                  selectedPoiStatus={poiStatus}
                  onChange={setPoiStatus}
                />
              </Col>
              <Col>
                <PoiTypeSelect
                  poiTypes={filteredPoiTypes}
                  selectedPoiType={poiType}
                  onChange={setPoiType}
                />
              </Col>
              <Col>
                <CitySelect
                  citys={citys}
                  selectedCity={city}
                  onChange={setCity}
                />
              </Col>
            </Row>

            <Row className="margin-top-20">
              <Col>
                <FormGroup>
                  <FormLabel htmlFor="labelFr">
                    {t('LABEL')}{' '}
                    <Image
                      src="/images/icons/flag_fr_icon_24.png"
                      className="flag-label"
                      alt="Label français"
                    />
                  </FormLabel>
                  <FormControl
                    id="labelFr"
                    type="text"
                    placeholder="Saisir libellé"
                    value={labels.labelFr}
                    onChange={(e) =>
                      setLabels({ ...labels, labelFr: e.target.value })
                    }
                  />
                </FormGroup>
              </Col>
              <Col>
                <FormGroup>
                  <FormLabel htmlFor="labelEn">
                    {t('LABEL')}{' '}
                    <Image
                      src="/images/icons/flag_en_icon_24.png"
                      className="flag-label"
                      alt="Label anglais"
                    />
                  </FormLabel>
                  <FormControl
                    id="labelEn"
                    type="text"
                    placeholder="Saisir libellé"
                    value={labels.labelEn}
                    onChange={(e) =>
                      setLabels({ ...labels, labelEn: e.target.value })
                    }
                  />
                </FormGroup>
              </Col>
              <Col>
                <FormGroup>
                  <FormLabel htmlFor="labelEs">
                    {t('LABEL')}{' '}
                    <Image
                      src="/images/icons/flag_es_icon_24.png"
                      className="flag-label"
                      alt="Label espagnol"
                    />
                  </FormLabel>
                  <FormControl
                    id="labelEs"
                    type="text"
                    placeholder="Saisir libellé"
                    value={labels.labelEs}
                    onChange={(e) =>
                      setLabels({ ...labels, labelEs: e.target.value })
                    }
                  />
                </FormGroup>
              </Col>
            </Row>

            <Row className="margin-top-20">
              <Col>
                <FormGroup>
                  <FormLabel htmlFor="descriptionFr">
                    Description{' '}
                    <Image
                      src="/images/icons/flag_fr_icon_24.png"
                      className="flag-label"
                      alt="Label français"
                    />
                  </FormLabel>
                  <FormControl
                    as="textarea"
                    id="descriptionFr"
                    placeholder="Description en français"
                    value={descriptions.descriptionFr}
                    onChange={(e) =>
                      setDescriptions({
                        ...descriptions,
                        descriptionFr: e.target.value,
                      })
                    }
                  />
                </FormGroup>
              </Col>
              <Col>
                <FormGroup>
                  <FormLabel htmlFor="descriptionEn">
                    Description{' '}
                    <Image
                      src="/images/icons/flag_en_icon_24.png"
                      className="flag-label"
                      alt="Label anglais"
                    />
                  </FormLabel>
                  <FormControl
                    as="textarea"
                    id="descriptionEn"
                    placeholder="Description en anglais"
                    value={descriptions.descriptionEn}
                    onChange={(e) =>
                      setDescriptions({
                        ...descriptions,
                        descriptionEn: e.target.value,
                      })
                    }
                  />
                </FormGroup>
              </Col>
              <Col>
                <FormGroup>
                  <FormLabel htmlFor="descriptionEs">
                    Description{' '}
                    <Image
                      src="/images/icons/flag_es_icon_24.png"
                      className="flag-label"
                      alt="Label espagnol"
                    />
                  </FormLabel>
                  <FormControl
                    as="textarea"
                    id="descriptionEs"
                    placeholder="Description en espagnol"
                    value={descriptions.descriptionEs}
                    onChange={(e) =>
                      setDescriptions({
                        ...descriptions,
                        descriptionEs: e.target.value,
                      })
                    }
                  />
                </FormGroup>
              </Col>
            </Row>

            <Row className="margin-top-20">
              <Col>
                <FormGroup>
                  <FormLabel htmlFor="youtubeVideoUrl">Vidéo Youtube</FormLabel>
                  <FormControl
                    id="youtubeVideoUrl"
                    type="text"
                    placeholder="Id Youtube de la vidéo"
                    value={youtubeVideoUrl}
                    onChange={(e) => setYoutubeVideoUrl(e.target.value)}
                  />
                </FormGroup>
              </Col>
            </Row>

            {isWildlifePoi && (
              <Row className="margin-top-20">
                <Col>
                  <TypeSelect
                    value={type || 'WILD_ANIMAL'}
                    onChange={setType}
                  />
                </Col>
              </Row>
            )}

            {isMountainBikingPoi && (
              <Row className="margin-top-20">
                <Col>
                  <TechnicitySelect
                    value={technicity || 'EASY'}
                    onChange={setTechnicity}
                  />
                </Col>
                <Col>
                  <SurfaceTypeSelect
                    value={surfaceType || 'GRAVEL_PATH'}
                    onChange={setSurfaceType}
                  />
                </Col>
                <Col></Col>
              </Row>
            )}

            {hasAltitudePoi && (
              <Row className="margin-top-20">
                <Col>
                  <FormGroup controlId="altitude">
                    <FormLabel>{t('ALTITUDE')}</FormLabel>
                    <FormControl
                      type="number"
                      value={altitude}
                      onChange={(e) => setAltitude(e.target.value)}
                    />
                  </FormGroup>
                </Col>
                <Col></Col>
                <Col></Col>
              </Row>
            )}

            {hasPhoneAndEmailPoi && (
              <Row className="margin-top-20">
                <Col>
                  <FormGroup controlId="phone">
                    <FormLabel>{t('PHONE')}</FormLabel>
                    <FormControl
                      type="text"
                      placeholder="Saisir téléphone"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                    />
                  </FormGroup>
                </Col>
                <Col>
                  <FormGroup controlId="email">
                    <FormLabel>{t('EMAIL_LABEL')}</FormLabel>
                    <FormControl
                      type="email"
                      placeholder="Saisir email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                    />
                  </FormGroup>
                </Col>
                <Col></Col>
              </Row>
            )}

            {isEvent && (
              <Row className="margin-top-20">
                <Col>
                  <FormGroup>
                    <FormLabel htmlFor="startDate">
                      {t('START_DATE')}:{' '}
                    </FormLabel>
                    <DatePicker
                      id="startDate"
                      locale="fr"
                      selected={startDate}
                      onChange={(date) => setStartDate(date)}
                      showTimeSelect
                      timeFormat="HH:mm"
                      timeIntervals={15}
                      dateFormat="dd/MM/yyyy HH:mm"
                      placeholderText={t('START_DATE')}
                      className="form-control"
                    />
                  </FormGroup>
                </Col>
                <Col>
                  <FormGroup>
                    <FormLabel htmlFor="endDate">{t('END_DATE')}: </FormLabel>
                    <DatePicker
                      id="endDate"
                      locale="fr"
                      selected={endDate}
                      onChange={(date) => setEndDate(date)}
                      showTimeSelect
                      timeFormat="HH:mm"
                      timeIntervals={15}
                      dateFormat="dd/MM/yyyy HH:mm"
                      placeholderText={t('END_DATE')}
                      className="form-control"
                    />
                  </FormGroup>
                </Col>
              </Row>
            )}

            {!isEvent && (
              <>
                <h2 style={{ marginTop: '30px' }}>
                  Critères d'attachement de ce POI à d'autres POI
                </h2>
                <Row className="margin-top-20">
                  <Col>
                    <PoiTypeMultiSelect
                      poiTypes={poiTypes}
                      selectedPoiTypes={formData.linkCriteriaPoiTypes}
                      handleButtonClick={handlePoiTypesChange}
                    />
                  </Col>
                  <Col>
                    <CityMultiSelect
                      citys={citys}
                      selectedCitys={formData.linkCriteriaCitys}
                      handleButtonClick={handleCitysChange}
                    />
                  </Col>
                  <Col>
                    <FormGroup className="d-flex align-items-center">
                      <FormLabel
                        htmlFor="distanceSelect"
                        style={{ width: '160px' }}
                      >
                        Distance maxi
                      </FormLabel>
                      <FormControl
                        as="select"
                        id="distanceSelect"
                        value={distance}
                        onChange={handleDistanceChange}
                      >
                        <option value={50000}>Toute distance</option>
                        <option value={100}>100m</option>
                        <option value={200}>200m</option>
                        <option value={500}>500m</option>
                        <option value={1000}>1km</option>
                        <option value={5000}>5km</option>
                      </FormControl>
                    </FormGroup>
                  </Col>
                </Row>
              </>
            )}

            <h2 style={{ marginTop: '30px' }}>Sur la carte</h2>
            <GoogleMapEdit poi={poi} onUpdateLocation={onUpdateLocation} />

            <Row className="margin-top-20 text-center">
              <Col>
                <Button variant="primary" onClick={handleSaveChanges}>
                  {t('CONFIRM')}
                </Button>
                <Link
                  className="btn btn-secondary margin-left-10"
                  to={POI_PATH}
                >
                  {t('CANCEL')}
                </Link>
              </Col>
            </Row>
          </>
        )}
      </Container>
      <Footer />
    </>
  )
}

export default ManagePoiEdit
